import { action, computed, makeObservable, override } from 'mobx';
import { createContext, useContext } from 'react';
import { SubscriberDomainParams } from 'global/hooks/useSubscriberDomainParams';
import { redirectToRoute } from 'routes/redirectToRoute';
import { SubscriberRouteV2Id } from 'routes/routesConfigV2';
import EmployeeFlowDelegate from 'store/flows/subscriber/employeeFlowDelegate';
import IHealthQuestionnairePage from 'store/modules/health-questionnaire/iHealthQuestionnairePage';

export default class HealthQuestionnairePageStore extends IHealthQuestionnairePage {
  public readonly flowDelegate: EmployeeFlowDelegate;

  public get isInitialLoading(): boolean {
    return this.flowDelegate.isFlowDataLoading || this.isPageContentLoading;
  }

  public editCategory(categoryName: string): void {
    const { groupId, subscriberId, entityId } = this.domainParams;

    redirectToRoute(SubscriberRouteV2Id.SUBSCRIBER_HEALTH_QUESTIONNAIRE_MEDICAL_CATEGORY, {
      groupId,
      subscriberId,
      entityId,
      category: encodeURIComponent(categoryName),
    });
  }

  protected onCompleteRedirect(): void {
    this.flowDelegate.onCompleteRedirect();
  }

  protected override setIsActionLoading(status: boolean): void {
    super.setIsActionLoading(status);
    this.flowDelegate.setIsActionLoading(status);
  }

  constructor(domainParams: SubscriberDomainParams) {
    super(domainParams);
    this.flowDelegate = new EmployeeFlowDelegate({ domainParams });

    makeObservable<HealthQuestionnairePageStore, 'onCompleteRedirect' | 'setIsActionLoading'>(this, {
      isInitialLoading: computed,
      editCategory: action.bound,
      onCompleteRedirect: action.bound,
      setIsActionLoading: override,
    });
  }
}

const HealthQuestionnairePageStoreContext = createContext(
  new HealthQuestionnairePageStore({ groupId: '', subscriberId: '', entityId: '' }),
);

export const useHealthQuestionnairePageStore = (): HealthQuestionnairePageStore => {
  return useContext(HealthQuestionnairePageStoreContext);
};

export { HealthQuestionnairePageStoreContext };
